import { StatusCodes } from "http-status-codes";
import {
  IPatient,
  IAllPatientFetchResponse,
  IPatientFetchResponse,
  IPatientFilter,
  IPatientCasesFetchResponse,
  IPatientSchemeList,
  IPatientSchemeState,
  IFetchPatientBasicDetails,
  IFetchAllDetails,
  IPostPatientDetailsResponse,
  IPatientFetchDetailsFromPortal,
  IPatientPreAuthQueryGetDetails,
  UpdateQueryPayload,
  IVisitFilter,
  IPatientCasesWithVisitsFetchResponse,
  CasesWithAllVisitsOfPatient,
  IPatientDocDeficiencyFilter,
} from "../interfaces/IPatient";
import {
  API_NEW_URL,
  API_URL,
  CASES_APIS,
  CGHS_DROPDOWNS,
  DISCHARGE,
  DISCHARGE_SUMMARY,
  DOCUMENTS,
  DOWNLOAD_ATTACHMENTS,
  GET_ALL_DOCUMENTS_DATA,
  GET_ALL_STARHEALTH_DOCUMENTS_DATA,
  PATIENT_APIS,
  PROCEDURES,
  QUERY_APIS,
  UPDATE_ALL_DOCUMENTS_DATA,
} from "../utils/constants";
import { ICase } from "../interfaces/ICase";
import Axios from "../utils/axiosInterceptor";
import {
  EnhancementAction,
  EnhancementResponse,
} from "../interfaces/IEnhancement";
import { DischargeResponse } from "../interfaces/IDischarge";
import {
  IClaimRes,
  IClaimSubmissionResponse,
} from "../interfaces/IPreAuthClaim";
import { InputNumberToken } from "antd/es/input-number/style/token";
import { errorMessage, successMessage } from "../utils/notificationUtils";
import axios from "axios";
import { VerticalRightOutlined } from "@ant-design/icons";
import { VisitSchemeStageDocuments } from "../interfaces/ISchemeOnboarding";
import {
  IAllQueryFetchResponse,
  IQueryFilter,
} from "../interfaces/IPreAuthQuery";

export const deleteProcedure = async (portalId: number, data: any) => {
  try {
    const response = await Axios.put(
      `${PATIENT_APIS.DELETE_PROCEDURE}/${portalId}/casePreAuthCGHS_Procedure`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteReferral = async (
  caseId: number,
  referralId: number,
  data: any
) => {
  try {
    const response = await Axios.patch(
      `${CASES_APIS.CASE_REFERRAL}/${caseId}/referralInfo/${referralId}`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

//for fetching medco doctor list
export const fetchDoctorList = async () => {
  try {
    const response = await Axios.get(`${CGHS_DROPDOWNS.DOCTOR_LIST}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data || "Unable to do fetch doctor list");
  }
};

//for fetching govt hospital list
export const fetchGovtHospitalList = async () => {
  try {
    const response = await Axios.get(`${CGHS_DROPDOWNS.GOVT_HOSPITAL_LIST}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data || "Unable to do fetch govt hospital list"
    );
  }
};

//for fetching diagnosis list
export const fetchDiagnosisList = async () => {
  try {
    const response = await Axios.get(`${CGHS_DROPDOWNS.DIAGNOSIS_LIST}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data || "Unable to do fetch diagnosis list"
    );
  }
};

export const fetchDoctorListNew = async () => {
  try {
    const response = await Axios.get(`${CGHS_DROPDOWNS.DOCTOR_LIST_NEW}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data || "Unable to do fetch doctor list");
  }
};

//for fetching govt hospital list
export const fetchGovtHospitalListNew = async () => {
  try {
    const response = await Axios.get(
      `${CGHS_DROPDOWNS.GOVT_HOSPITAL_LIST_NEW}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data || "Unable to do fetch govt hospital list"
    );
  }
};

//for fetching diagnosis list
export const fetchDiagnosisListNew = async () => {
  try {
    const response = await Axios.get(`${CGHS_DROPDOWNS.DIAGNOSIS_LIST_NEW}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data || "Unable to do fetch diagnosis list"
    );
  }
};

export const fetchPatients = async (
  filters: IPatientFilter,
  healthCareId: number
): Promise<IAllPatientFetchResponse> => {
  try {
    const response = await Axios.post<IAllPatientFetchResponse>(
      `${PATIENT_APIS.ALL_PATIENTS}/healthCareId/${healthCareId}`,
      filters
    );

    if (response.status === StatusCodes.OK) {
      return {
        patientResponseList: response.data.patientResponseList,
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        status: response.status,
      };
    } else {
      return {
        errorMessage: "Cannot fetch all patients",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error fetching all patients:", error);
    throw new Error("Cannot fetch all patients");
  }
};

export const fetchPatientsPL = async (
  filters: any,
  healthCareId: number
): Promise<IAllPatientFetchResponse> => {
  try {
    const response = await Axios.get(PATIENT_APIS.ALL_PATIENTS_PL, {
      headers: {
        "Content-Type": "application/json",
        endDate: filters.endDate,
        orderBy: filters.orderBy,
        pageNumber: filters.pageNumber.toString(),
        pageSize: filters.pageSize.toString(),
        search: filters.searchTerm || "",
        startDate: filters.startDate,
        state: filters.state ? filters.state.map(String) : null,
        scheme: filters.scheme ? filters.scheme.map(String) : null,
        visitType: filters.visitType
          ? filters.visitType.map(String)
          : undefined,
        status: filters.status?.toString(),
        stage: filters.stage,
        selectedDateType: filters.selectedDateType || "DOA",
        healthCareId: healthCareId,
        all: Number(healthCareId) === -1 ? "yes" : "no",
        assignedToMe: filters.assignedToMe || "no",
        pending: filters.pending,
        pwaRegister: true
      },
    });
    if (response.status === StatusCodes.OK) {
      return {
        stagesCount: response.data.stagesCount,
        patientResponseList: response.data.patientCaseVisitDTOList,
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        status: response.status,
      };
    } else if (response.status === StatusCodes.UNPROCESSABLE_ENTITY) {
      errorMessage("Something went wrong. Please, Try again later");
      return {
        errorMessage: "Something went wrong. Please, Try again later",
        status: response.status,
      };
    } else {
      return {
        errorMessage: "Cannot fetch all patients",
        status: response.status,
      };
    }
  } catch (error) {
    errorMessage("Something went wrong. Please, Try again later");
    console.error("Error fetching all patients:", error);
    throw new Error("Cannot fetch all patients");
  }
};

export const downloadExcelPL = async (
  filters: IPatientFilter,
  healthCareId: number
) => {
  try {
    const response = await Axios.get(PATIENT_APIS.DOWNLOAD_PATIENTS_PL_EXCEL, {
      headers: {
        "Content-Type": "application/json",
        endDate: filters.endDate,
        orderBy: filters.orderBy,
        search: filters.searchTerm || "",
        startDate: filters.startDate,
        state: filters.state ? filters.state.map(String) : null,
        scheme: filters.scheme ? filters.scheme.map(String) : null,
        visitType: filters.visitType
          ? filters.visitType.map(String)
          : undefined,
        status: filters.status?.toString(),
        stage: filters.stage,
        selectedDateType: filters.selectedDateType,
        healthCareId: healthCareId,
        all: Number(healthCareId) === -1 ? "yes" : "no",
        assignedToMe: filters.assignedToMe || "no",
      },
      responseType: "arraybuffer",
    });

    const contentDisposition = response.headers["content-disposition"];
    const filename = contentDisposition
      ? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
      : "patients_data.xlsx";

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Create a link element to initiate the download
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error in downloading excel of all patients:", error);
    throw new Error("Error in downloading excel of all patients");
  }
};

export const fetchQueriesPL = async (
  filters: IQueryFilter,
  healthCareId: number
): Promise<IAllQueryFetchResponse> => {
  try {
    const response = await Axios.get(QUERY_APIS.ALL_QUERIES_PL, {
      headers: {
        "Content-Type": "application/json",
        endDate: filters.endDate,
        orderBy: filters.orderBy,
        pageNumber: filters.pageNumber.toString(),
        pageSize: filters.pageSize.toString(),
        search: filters.searchTerm || null,
        startDate: filters.startDate,
        state: filters.state ? filters.state.map(String) : null,
        scheme: filters.scheme ? filters.scheme.map(String) : null,
        visitType:
          filters.visitType && filters.visitType?.length > 0
            ? filters.visitType?.map(String)
            : null,
        status: filters.status,
        stage: filters.stage,
        selectedDateType: filters.selectedDateType,
        healthCareId: healthCareId,
        all: Number(healthCareId) === -1 ? "yes" : "no",
        typeOfQuery: filters.typeOfQuery
          ? filters.typeOfQuery.map(String)
          : null,
      },
    });
    if (response.status === StatusCodes.OK) {
      return {
        stagesCount: response.data.stagesCount,
        queryResponseList: response.data.queriesDataList,
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        status: response.status,
        overallQueryCount: response.data.overallQueryCount,
        visitStageId: response.data.queryStage,
      };
    } else {
      return {
        errorMessage: "Cannot fetch all queries",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error fetching all queries:", error);
    throw new Error("Cannot fetch all queries");
  }
};

export const fetchPatientDoCDeficiency = async (
  filters: any,
  healthCareId: number
): Promise<IAllPatientFetchResponse> => {
  try {
    const response = await Axios.post<any>(
      `${PATIENT_APIS.ALL_PATIENTS}/healthCares/${healthCareId}/documentDeficiency`,
      {
        ...filters,
        all: Number(healthCareId) === -1 ? "yes" : "no",
        visitType: filters.visitType
          ? filters.visitType.map(String)
          : undefined,
      }
    );

    if (response.status === StatusCodes.OK) {
      return {
        patientResponseList: response.data.patientResponseList,
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        status: response.status,
      };
    } else {
      return {
        errorMessage: "Cannot fetch all patients",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error fetching all patients:", error);
    throw new Error("Cannot fetch all patients");
  }
};

export const fetchVisits = async (filters: IVisitFilter) => {
  try {
    const response = await Axios.post(PATIENT_APIS.GET_ALL_VISITS, filters);

    if (response.status === StatusCodes.OK) {
      return {
        visitInfoResponseList: response.data.visitInfoResponseList,
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        status: response.status,
      };
    } else {
      return {
        errorMessage: "Cannot fetch all visits",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error fetching all visits:", error);
    throw new Error("Cannot fetch all visits");
  }
};

export const fetchAllDetailsCount = async (healthcareId: number) => {
  try {
    const response = await Axios.get(
      PATIENT_APIS.ALL_COUNT_CARDS(healthcareId)
    );
    if (response.status === StatusCodes.OK) return response.data;
  } catch (error) {
    console.error("Error fetching details count:", error);
    throw new Error("Cannot fetch details count");
  }
};

export const fetchTimeline = async (visit_id: number, case_id: number) => {
  try {
    const response = await Axios.get(
      `${PATIENT_APIS.TIMELINE(visit_id, case_id)}`
    );
    if (response.status === StatusCodes.OK) return response.data;
  } catch (error) {
    console.error("Error fetching timeline:", error);
    throw new Error("Cannot fetch timeline");
  }
};

// export const fetchPortalStatusList = async (schemeId: number) => {
//   try {
//     const response = await Axios.get(
//       `${PATIENT_APIS.PORTAL_STATUS_LIST(schemeId)}`
//     );
//     if (response.status === StatusCodes.OK) return response.data;
//   } catch (error) {
//     console.error("Error fetching visit stage status:", error);
//     throw new Error("Cannot fetch visit stage status");
//   }
// };

export const fetchVisitStageStatus = async (visitStageId: number) => {
  try {
    const response = await Axios.get(
      `${PATIENT_APIS.VISIT_STAGE_STATUS(visitStageId)}`
    );
    if (response.status === StatusCodes.OK) return response.data;
  } catch (error) {
    console.error("Error fetching visit stage status:", error);
    throw new Error("Cannot fetch visit stage status");
  }
};

export const fetchPortalStatusList = async (schemeId: number) => {
  try {
    const response = await Axios.get(
      `${PATIENT_APIS.PORTAL_STATUS_LIST(schemeId)}`
    );
    if (response.status === StatusCodes.OK) return response.data;
  } catch (error) {
    console.error("Error fetching visit stage status:", error);
    throw new Error("Cannot fetch visit stage status");
  }
};

export const updateVisitStageStatus = async (
  visit_id: number,
  visit_stage_id: number,
  state_id: any
) => {
  try {
    const response = await Axios.patch(
      `${PATIENT_APIS.UPDATE_VISIT_STAGE_STATUS(
        visit_id,
        visit_stage_id,
        state_id
      )}`
    );
    if (response.status === StatusCodes.OK) return response.data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data || "Cannot update visit stage status"
    );
  }
};

export const updateVisitStageStatusReferralAcceptance = async (
  visit_id: number,
  visit_stage_id: number,
  state_id: any,
  payload: any
) => {
  try {
    const response = await Axios.patch(
      `${PATIENT_APIS.UPDATE_VISIT_STAGE_STATUS(
        visit_id,
        visit_stage_id,
        state_id
      )}`, payload
    );
    if (response.status === StatusCodes.OK) return response.data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data || "Cannot update visit stage status"
    );
  }
};

export const updatePortalStatus = async (
  visit_id: number,
  portal_status_scheme: any
) => {
  try {
    const response = await Axios.patch(
      `${PATIENT_APIS.UPDATE_PORTAL_STATUS(visit_id, portal_status_scheme)}`
    );
    if (response.status === StatusCodes.OK) return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchVisitTypes = async () => {
  try {
    const response = await Axios.get(PATIENT_APIS.VISIT_TYPES);
    if (response.status === StatusCodes.OK) return response.data;
  } catch (error) {
    console.error("Error fetching visit types:", error);
    throw new Error("Cannot fetch visit types");
  }
};

export const fetchAllVisitTypes = async () => {
  try {
    const response = await Axios.get(PATIENT_APIS.ALL_VISIT_TYPES);
    if (response.status === StatusCodes.OK) return response.data;
  } catch (error) {
    console.error("Error fetching visit types:", error);
    throw new Error("Cannot fetch visit types");
  }
};

export const fetchAllVisitStages = async () => {
  try {
    const response = await Axios.get(PATIENT_APIS.ALL_VISIT_STAGES);
    if (response.status === StatusCodes.OK) return response.data;
  } catch (error) {
    console.error("Error fetching visit types:", error);
    throw new Error("Cannot fetch visit types");
  }
};

export const fetchFilteredVisitStages = async () => {
  try {
    const response = await Axios.get(PATIENT_APIS.FILTERED_VISIT_STAGES);
    if (response.status === StatusCodes.OK) return response.data;
  } catch (error) {
    console.error("Error fetching visit types:", error);
    throw new Error("Cannot fetch visit types");
  }
};

export const fetchQueryFilteredVisitStages = async () => {
  try {
    const response = await Axios.get(QUERY_APIS.FILTERED_VISIT_STAGES);
    if (response.status === StatusCodes.OK) return response.data;
  } catch (error) {
    console.error("Error fetching visit types:", error);
    throw new Error("Cannot fetch visit types");
  }
};

export const fetchAllStates = async () => {
  try {
    const response = await Axios.get(PATIENT_APIS.ALL_STATE);
    if (response.status === StatusCodes.OK) return response.data;
  } catch (error) {
    console.error("Error fetching states:", error);
    throw new Error("Cannot fetch states");
  }
};

export const fetchAllDocumentTypes = async () => {
  try {
    const response = await Axios.get(PATIENT_APIS.ALL_DOCUMENT_TYPES);
    if (response.status === StatusCodes.OK) return response.data;
  } catch (error) {
    console.error("Error fetching documents types:", error);
    throw new Error("Cannot fetch documents types");
  }
};

export const createVisitSchemeStageDocuments = async (
  requestData: VisitSchemeStageDocuments
) => {
  try {
    const response = await Axios.post(
      PATIENT_APIS.VISIT_SCHEME_STAGE_DOCUMENTS,
      requestData
    );
    if (response.status === StatusCodes.OK) return response.data;
  } catch (error) {
    console.error("Error creating visit scheme stage documents:", error);
    throw new Error("Error creating visit scheme stage documents");
  }
};

export const fetchPatientById = async (
  id: number
): Promise<IPatientFetchResponse> => {
  try {
    const response = await Axios.get<IPatient>(
      `${PATIENT_APIS.PATIENT_DETAILS}/${id}`
    );

    if (response.status === StatusCodes.OK) {
      return { patient: response.data, status: response.status };
    } else {
      return {
        errorMessage: "Cannot fetch patients details",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error fetching patient details:", error);
    throw new Error("Cannot fetch patients detail");
  }
};

export const getShiftPatient = async (caseId: number, visitId: number) => {
  try {
    const response = await Axios.get(
      `${PATIENT_APIS.GET_SHIFT_PATIENT}/${caseId}/visit_info_id/${visitId}/visit-shift`
    );

    if (response.status === StatusCodes.OK) {
      return response.data;
    } else {
      return {
        errorMessage: "Cannot fetch patient shift details",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error fetching patient shift details:", error);
    errorMessage("Please, Try again after sometime");
    throw new Error("Cannot fetch patients shift detail");
  }
};

export const postShiftPatient = async (formData: any) => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.POST_SHIFT_PATIENT}`,
      formData
    );

    if (response.status === StatusCodes.OK) {
      return response.data;
    } else {
      return {
        errorMessage: "Cannot post patient shift details",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error in post patient shift details:", error);
    errorMessage("Please, Try again after sometime");
    throw new Error("Cannot post patients shift detail");
  }
};

export const updateShiftPatient = async (
  formData: any,
  visitShiftId: number
) => {
  try {
    const response = await Axios.patch(
      `${PATIENT_APIS.UPDATE_SHIFT_PATIENT}/${visitShiftId}`,
      formData
    );

    if (response.status === StatusCodes.OK) {
      return response.data;
    } else {
      return {
        errorMessage: "Cannot update patient shift details",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error in update patient shift details:", error);
    errorMessage("Please, Try again after sometime");
    throw new Error("Cannot update patients shift detail");
  }
};

export const modifyPatientDetails = async (id: number, formValues: object) => {
  try {
    const response = await Axios.patch<IPatient>(
      `${PATIENT_APIS.PATIENT_DETAILS}/${id}`,
      formValues
    );

    if (response.status === StatusCodes.OK) {
      return { patient: response.data, status: response.status };
    } else {
      return {
        errorMessage: "Cannot update patients details",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error updating patient details:", error);
    throw new Error("Cannot update patients details");
  }
};

export const addPatientDetails = async (formValues: object): Promise<any> => {
  try {
    const response = await Axios.post<IPatient>(
      // PATIENT_APIS.PATIENT_DETAILS,
      PATIENT_APIS.CREATE_PATIENT,
      formValues
    );

    if (response.status === StatusCodes.OK || response.status === 208) {
      return { patient: response.data, status: response.status };
    } else {
      return {
        errorMessage: response?.data?.message || "PATIENT ALREADY EXIST",
        status: response.status,
      };
    }
  } catch (error: any) {
    console.error("Error posting patient details:", error);
    throw new Error(
      error?.response?.data || "Please, Try again after sometime"
    );
  }
};

export const fetchPatientCases = async (
  id: number
): Promise<IPatientCasesFetchResponse> => {
  try {
    const response = await Axios.get<Array<ICase>>(
      `${PATIENT_APIS.PATIENT_DETAILS}/${id}/casesNew/all`
    );

    if (response.status === StatusCodes.OK) {
      return { cases: response.data, status: response.status, patientId: id };
    } else {
      return {
        cases: [],
        patientId: id,
        errorMessage: "Cannot fetch patients details",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error fetching patient cases:", error);
    throw new Error("Cannot fetch patients detail");
  }
};

export const fetchPatientCasesWithVisits = async (
  id: number,
  billType: string
): Promise<IPatientCasesWithVisitsFetchResponse> => {
  try {
    const response = await Axios.get<Array<CasesWithAllVisitsOfPatient>>(
      `${PATIENT_APIS.PATIENT_DETAILS}/${id}/casesVisits/all?billType=${billType}`
    );

    if (response.status === StatusCodes.OK) {
      return { cases: response.data, status: response.status, patientId: id };
    } else {
      return {
        cases: [],
        patientId: id,
        errorMessage: "Cannot fetch patients details",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error fetching patient cases:", error);
    throw new Error("Cannot fetch patients detail");
  }
};

export const fetchPatientSchemes = async (
  id: number
): Promise<IPatientSchemeList> => {
  try {
    const response = await Axios.get<Array<IPatientSchemeState>>(
      `${PATIENT_APIS.PATIENT_SCHEMES}/${id}/patient_schemes/all`
    );

    return { schemeNames: response.data, status: response.status };
  } catch (error) {
    console.error("Error fetching patient schemes:", error);
    throw new Error("Cannot fetch patient details");
  }
};

export const getPatientBasicDetails = async (
  id: any,
  docTypes?: Array<string | number>
): Promise<IFetchPatientBasicDetails> => {
  try {
    const response = await Axios.get<IFetchAllDetails>(
      `${PATIENT_APIS.CASE_DETAILS_VISIT_ID}/${id}`,
      {
        params: {
          docTypes,
        },
        paramsSerializer: (params) => {
          return docTypes?.map((id) => `docTypes=${id}`).join("&") || "";
        },
      }
    );

    return { fetchBasicDetail: response.data, status: response.status };
  } catch (error) {
    console.error("Error fetching patient basic details:", error);
    throw new Error("Cannot fetch patient details");
  }
};
export const getPatientBasicDetails2 = async (
  id: number
): Promise<IFetchPatientBasicDetails> => {
  try {
    const response = await Axios.get<IFetchAllDetails>(
      `${PATIENT_APIS.CASE_DETAILS_VISIT_ID}/${id}`
    );

    return { fetchBasicDetail: response.data, status: response.status };
  } catch (error) {
    console.error("Error fetching patient basic details:", error);
    throw new Error("Cannot fetch patient details");
  }
};
export const postSchemeToGetPatient = async (
  data: IPatientFetchDetailsFromPortal
) => {
  try {
    const response = await Axios.post<IPatient>(
      PATIENT_APIS.POST_API_TO_GET_PATIENT,
      data
    );

    return { patient: response.data, status: response.status };
  } catch (error: any) {
    if (
      error?.response?.data
        ?.toLowerCase()
        ?.includes("something went wrong from fetching the details from portal")
    ) {
      throw new Error("User doesn't exist at portal");
    }
    console.error("Error fetching patient details from portal:", error);
    throw new Error("Cannot fetch patient details");
  }
};

export const getPreAuthQueryData = async (
  data: string,
  caseId: number | undefined,
  visitInfoId: number | undefined,
  schemeMetaDataId: number | undefined
): Promise<IPatientPreAuthQueryGetDetails> => {
  try {
    const response = await Axios.get<
      IPatientPreAuthQueryGetDetails["queryData"]
    >(
      `${PATIENT_APIS.GET_PRE_AUTH_QUERY_DATA}/${caseId}/visitInfo/${visitInfoId}/portalMetaDataId/${schemeMetaDataId}/casePreAuthQueries`,
      {}
    );

    return { queryData: response.data };
  } catch (error) {
    console.error("Error fetching patient details from portal:", error);
    throw new Error("Cannot fetch patient details");
  }
};

export const updatePreAuthQuery = async (
  queryId: string,
  payload: any,
  caseId: number | undefined,
  visitInfoId: number | undefined,
  schemeMetaDataId: number | undefined
): Promise<void> => {
  try {
    const response = await Axios.patch(
      `${PATIENT_APIS.GET_PRE_AUTH_QUERY_DATA}/${caseId}/visitInfo/${visitInfoId}/portalMetaDataId/${schemeMetaDataId}/casePreAuthQueries/${queryId}`,
      payload
    );

    if (response.status !== 200) {
      throw new Error("Failed to update pre-auth query");
    }
  } catch (error) {
    console.error("Error updating pre-auth query:", error);
    throw error;
  }
};

export const downloadAllAttachments = async (
  visitId: number,
  stageId: number
) => {
  try {
    const res: any = await Axios.get(
      `${DOWNLOAD_ATTACHMENTS(stageId, visitId)}`,
      {
        responseType: "arraybuffer",
      }
    );
    if (res?.data) {
      const contentDisposition = res.headers["content-disposition"];
      let fileName = "attachments.zip";

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          fileName = match[1];
        }
      }
      const blob = new Blob([res.data], { type: "application/zip" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);

      return "success";
    } else {
      return "failed";
    }
  } catch (error) {
    console.error("Error downloading attachments:", error);
    return "failed";
  }

  // try {
  //   const res: any = await Axios.get(
  //     `${DOWNLOAD_ATTACHMENTS(stageId, visitId)}`,
  //     {
  //       responseType: "arraybuffer",
  //     }
  //   );

  //   if (res?.data) {
  //     const blob = new Blob([res.data], { type: "application/zip" });
  //     const downloadUrl = window.URL.createObjectURL(blob);
  //     const a = document.createElement("a");
  //     a.href = downloadUrl;
  //     a.download = `attachments.zip`;
  //     document.body.appendChild(a);
  //     a.click(); // Trigger the download
  //     document.body.removeChild(a);
  //     window.URL.revokeObjectURL(downloadUrl);
  //     return "success";
  //   } else {
  //     return "failed";
  //   }
  // } catch (error) {
  //   console.error("Error downloading attachments:", error);
  //   return "failed";
  // }
};

export const getAllDocumentsData = async (
  caseId: number,
  visitId: number,
  portalMetaDataId: number
) => {
  try {
    const res = await Axios.get(
      GET_ALL_DOCUMENTS_DATA(caseId, visitId, portalMetaDataId)
    );
    return res?.data;
  } catch (err: any) {
    console.error("Failed to Get All Documents:", err);
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "Please, try again later.";
    errorMessage(message);

    throw new Error(message);
  }
};

export const getAllStarhealthDocumentsData = async (
  caseId: number,
  visitId: number,
  schemeId: number
) => {
  try {
    const res = await Axios.get(
      GET_ALL_STARHEALTH_DOCUMENTS_DATA(caseId, visitId, schemeId)
    );
    return res?.data;
  } catch (err: any) {
    console.error("Failed to Get All Documents:", err);
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "Please, try again later.";
    errorMessage(message);

    throw new Error(message);
  }
};

export const updateAllDocumentsData = async (
  caseId: number,
  visitId: number,
  data: any
) => {
  try {
    const res = await Axios.post(
      UPDATE_ALL_DOCUMENTS_DATA(caseId, visitId),
      data
    );
    return res.data;
  } catch (err: any) {
    console.error("Failed to Update All Documents:", err);
    errorMessage(err?.response?.data || "Please, Try again after sometime");
    throw new Error("Failed to Update All Documents");
  }
};

export const enhancementService = {
  getPortalMetaData: async (schemeMetaDataId: number): Promise<any> => {
    try {
      const response = await Axios.get(
        `api/scheme_meta_data/${schemeMetaDataId}/scheme_onboarding`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching portal meta data:", error);
      throw error;
    }
  },

  getEnhancementData: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalMetaDataId: number | null
  ): Promise<EnhancementResponse> => {
    try {
      const response = await Axios.get<EnhancementResponse>(
        `${PATIENT_APIS.GET_ENHANCEMENT_DATA}/${caseId}/visitInfo/${visitInfoId}/portalMetaData/${portalMetaDataId}/caseEnhancement`
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching enhancement data:", error);
      throw error;
    }
  },
  saveEnhancementData: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalMetaDataId: number,
    caseEnhancementId: string | null,
    payload: any
  ): Promise<void> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.GET_ENHANCEMENT_DATA}/${caseId}/visitInfo/${visitInfoId}/portalMetaData`;

      // POST request
      response = await Axios.post(
        `${baseUrl}/${portalMetaDataId}/caseEnhancement`,
        payload
      );

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to save enhancement data");
      }
    } catch (error) {
      console.error("Error saving enhancement data:", error);
      throw error;
    }
  },
};

export const enhancementServiceIhx = {
  getPortalMetaData: async (schemeMetaDataId: number): Promise<any> => {
    try {
      const response = await Axios.get(
        `api/scheme_meta_data/${schemeMetaDataId}/scheme_onboarding`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching portal meta data:", error);
      throw error;
    }
  },

  getEnhancementData: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalMetaDataId: number | null
  ) => {
    try {
      const response = await Axios.get(
        `${PATIENT_APIS.IHX_SERVICE}/${caseId}/visitInfo/${visitInfoId}/portalMetaData/${portalMetaDataId}/caseEnhancement`
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching enhancement data:", error);
      throw new Error("Error fetching enhancement data");
    }
  },
  saveEnhancementData: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalMetaDataId: number,
    caseEnhancementId: string | null,
    payload: any
  ) => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.IHX_SERVICE}/${caseId}/visitInfo/${visitInfoId}/portalMetaData`;

      // POST request
      response = await Axios.post(
        `${baseUrl}/${portalMetaDataId}/caseEnhancement`,
        payload
      );

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to save enhancement data");
      }
    } catch (error) {
      console.error("Error saving enhancement data:", error);
      throw error;
    }
  },
};

export const dischargeService = {
  getDischargeData: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalId: number | undefined
  ): Promise<DischargeResponse> => {
    try {
      const response = await Axios.get<DischargeResponse>(
        `${PATIENT_APIS.GET_ENHANCEMENT_DATA}/${caseId}/visitInfo/${visitInfoId}/portalMetaData/${portalId}/caseDischarges`
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching enhancement data:", error);
      throw error;
    }
  },
  saveDischargeData: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalId: number | undefined,
    caseDischargeId: number | null,
    payload: any
  ): Promise<void> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.GET_ENHANCEMENT_DATA}/${caseId}/visitInfo/${visitInfoId}/portalMetaData`;

      if (caseDischargeId === null) {
        // POST request
        response = await Axios.post(
          `${baseUrl}/${portalId}/caseDischarges`,
          payload
        );
      } else {
        // PATCH request
        response = await Axios.patch(
          `${baseUrl}/${portalId}/caseDischarge/${caseDischargeId}/caseDischarges`,
          payload
        );
      }

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to save enhancement data");
      }
    } catch (error) {
      console.error("Error saving enhancement data:", error);
      throw error;
    }
  },
  saveDischargeDataInEdgePro: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalId: number | undefined,
    caseDischargeId: number | null,
    payload: any
  ): Promise<any> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.GET_ENHANCEMENT_DATA}/${caseId}/visitInfo/${visitInfoId}/portalMetaData`;
      // POST request
      if (caseDischargeId === null) {
        response = await Axios.post(
          `${baseUrl}/${portalId}/createCaseDischarge`,
          payload
        );
      } else {
        response = await Axios.patch(
          `${baseUrl}/${portalId}/caseDischarge/${caseDischargeId}/createCaseDischarge`,
          payload
        );
      }

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to save enhancement data");
      }

      return response;
    } catch (error) {
      console.error("Error saving enhancement data:", error);
      throw error;
    }
  },

  saveIntimateDischarge: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalMetaDataId: number | undefined,
    payload: any
  ): Promise<any> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.GET_ENHANCEMENT_DATA}/${caseId}/visitInfo/${visitInfoId}/portalMetaData/${portalMetaDataId}/createCaseDischargeIntimation`;
      // POST request

      response = await Axios.post(`${baseUrl}`, payload);

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to save enhancement data");
      }
      return response;
    } catch (error) {
      console.error("Error saving enhancement data:", error);
      // throw error;
      throw new Error("Failed to save enhancement data");
    }
  },
  updateClaimId: async (
    formData: any,
    caseId: number | undefined,
    visitId: number | undefined,
    schemeId: number | undefined
  ): Promise<any> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.GET_PRE_AUTH_QUERY_DATA}/${caseId}/visitInfo/${visitId}/schemeId/${schemeId}`;

      response = await Axios.patch(`${baseUrl}`, formData);

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to update claim Id ");
      }
      return response;
    } catch (error) {
      console.error("Failed to update claim Id:", error);
      // throw error;
      throw new Error("Failed to update claim Id");
    }
  },
  getDoctorTypeList: async (data: any) => {
    try {
      const response = await Axios.post(`${DISCHARGE.DOCTOR_TYPE_LIST}`, data);

      return response.data;
    } catch (error) {
      throw error;
    }
  },
  getStarHealthRoomTypeList: async (portalMetaDataId: number) => {
    try {
      const response = await Axios.get(
        `${DISCHARGE.ROOM_TYPE_LIST}/${portalMetaDataId}`
      );

      return response.data;
    } catch (error) {
      console.error("Error Room Type data:", error);
      throw error;
    }
  },

  dischargeSummaryReader: async (formData: any) => {
    try {
      const res = await Axios.post(
        `${DOCUMENTS.DISCHARGE_SUMMARY_READER}`,
        formData
      );
      return res?.data;
    } catch (err: any) {
      console.error("Failed to Read Discharge Summary Document:", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to Read Discharge Summary Document";
      errorMessage(message);

      throw new Error(message);
    }
  },
};

export const dischargeServiceNew = {
  getDischargeData: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalId: number | undefined
  ): Promise<DischargeResponse> => {
    try {
      const response = await Axios.get<DischargeResponse>(
        `${PATIENT_APIS.GET_ENHANCEMENT_DATA}/${caseId}/visitInfo/${visitInfoId}/portalMetaData/${portalId}/caseDischarges`
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching enhancement data:", error);
      throw error;
    }
  },
  saveDischargeData: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalId: number | undefined,
    caseDischargeId: number | null,
    payload: any
  ): Promise<void> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.GET_ENHANCEMENT_DATA}/${caseId}/visitInfo/${visitInfoId}/portalMetaData`;

      if (caseDischargeId === null) {
        // POST request
        response = await Axios.post(
          `${baseUrl}/${portalId}/caseDischarges`,
          payload
        );
      } else {
        // PATCH request
        response = await Axios.patch(
          `${baseUrl}/${portalId}/caseDischarge/${caseDischargeId}/caseDischarges`,
          payload
        );
      }

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to save enhancement data");
      }
    } catch (error) {
      console.error("Error saving enhancement data:", error);
      throw error;
    }
  },
  saveDischargeDataInEdgePro: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalId: number | undefined,
    caseDischargeId: number | null,
    payload: any
  ): Promise<any> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.GET_ENHANCEMENT_DATA}/${caseId}/visitInfo/${visitInfoId}/portalMetaData`;
      // POST request
      if (caseDischargeId === null) {
        response = await Axios.post(
          `${baseUrl}/${portalId}/createCaseDischarge`,
          payload
        );
      } else {
        response = await Axios.patch(
          `${baseUrl}/${portalId}/caseDischarge/${caseDischargeId}/createCaseDischarge`,
          payload
        );
      }

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to save enhancement data");
      }

      return response;
    } catch (error) {
      console.error("Error saving enhancement data:", error);
      throw error;
    }
  },

  saveIntimateDischarge: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalMetaDataId: number | undefined,
    payload: any
  ): Promise<any> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.GET_ENHANCEMENT_DATA}/${caseId}/visitInfo/${visitInfoId}/portalMetaData/${portalMetaDataId}/createCaseDischargeIntimation`;
      // POST request

      response = await Axios.post(`${baseUrl}`, payload);

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to save enhancement data");
      }
      return response;
    } catch (error) {
      console.error("Error saving enhancement data:", error);
      // throw error;
      throw new Error("Failed to save enhancement data");
    }
  },
  updateClaimId: async (
    formData: any,
    caseId: number | undefined,
    visitId: number | undefined,
    schemeId: number | undefined
  ): Promise<any> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.GET_PRE_AUTH_QUERY_DATA}/${caseId}/visitInfo/${visitId}/schemeId/${schemeId}`;

      response = await Axios.patch(`${baseUrl}`, formData);

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to update claim Id ");
      }
      return response;
    } catch (error) {
      console.error("Failed to update claim Id:", error);
      // throw error;
      throw new Error("Failed to update claim Id");
    }
  },
  getDoctorTypeList: async (data: any) => {
    try {
      const response = await Axios.post(
        `${DISCHARGE.DOCTOR_TYPE_LIST_NEW}`,
        data
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  },
  getStarHealthRoomTypeList: async (portalMetaDataId: number) => {
    try {
      const response = await Axios.get(
        `${DISCHARGE.ROOM_TYPE_LIST}/${portalMetaDataId}`
      );

      return response.data;
    } catch (error) {
      console.error("Error Room Type data:", error);
      throw error;
    }
  },

  dischargeSummaryReader: async (formData: any) => {
    try {
      const res = await Axios.post(
        `${DOCUMENTS.DISCHARGE_SUMMARY_READER}`,
        formData
      );
      return res?.data;
    } catch (err: any) {
      console.error("Failed to Read Discharge Summary Document:", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to Read Discharge Summary Document";
      errorMessage(message);

      throw new Error(message);
    }
  },
};

//ihx
export const dischargeServiceIhx = {
  getDischargeData: async (
    caseId: number | undefined,
    visitInfoId: number | undefined
  ) => {
    try {
      const response = await Axios.get(
        `${PATIENT_APIS.IHX_SERVICE}/${caseId}/visitInfo/${visitInfoId}/caseDischarges`
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching discharge data:", error);
      throw error;
    }
  },
  saveDischargeData: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalId: number | undefined,
    caseDischargeId: number | null,
    payload: any
  ): Promise<void> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.IHX_SERVICE}/${caseId}/visitInfo/${visitInfoId}/portalMetaData`;

      if (caseDischargeId === null) {
        // POST request
        response = await Axios.post(
          `${baseUrl}/${portalId}/caseDischarges`,
          payload
        );
      } else {
        // PATCH request
        response = await Axios.patch(
          `${baseUrl}/${portalId}/caseDischarge/${caseDischargeId}/caseDischarges`,
          payload
        );
      }

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to save enhancement data");
      }
    } catch (error) {
      console.error("Error saving enhancement data:", error);
      throw error;
    }
  },
  saveDischargeDataInEdgePro: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalId: number | undefined,
    caseDischargeId: number | null,
    payload: any
  ): Promise<any> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.IHX_SERVICE}/${caseId}/visitInfo/${visitInfoId}/portalMetaData`;
      // POST request
      if (caseDischargeId === null) {
        response = await Axios.post(
          `${baseUrl}/${portalId}/createCaseDischarge`,
          payload
        );
      } else {
        response = await Axios.patch(
          `${baseUrl}/${portalId}/caseDischarge/${caseDischargeId}/createCaseDischarge`,
          payload
        );
      }

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to save enhancement data");
      }

      return response;
    } catch (error) {
      console.error("Error saving enhancement data:", error);
      throw error;
    }
  },

  saveIntimateDischarge: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalMetaDataId: number | undefined,
    payload: any
  ): Promise<any> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.IHX_SERVICE}/${caseId}/visitInfo/${visitInfoId}/portalMetaData/${portalMetaDataId}/createCaseDischargeIntimation`;
      // POST request

      response = await Axios.post(`${baseUrl}`, payload);

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to save intimate discharge data");
      }
      return response;
    } catch (error) {
      console.error("Error saving intimate discharge data:", error);
      // throw error;
      throw new Error("Failed to save intimate discharge data");
    }
  },
  updateClaimId: async (
    formData: any,
    caseId: number | undefined,
    visitId: number | undefined,
    schemeId: number | undefined
  ): Promise<any> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.GET_PRE_AUTH_QUERY_DATA}/${caseId}/visitInfo/${visitId}/schemeId/${schemeId}`;

      response = await Axios.patch(`${baseUrl}`, formData);

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to update claim Id ");
      }
      return response;
    } catch (error) {
      console.error("Failed to update claim Id:", error);
      // throw error;
      throw new Error("Failed to update claim Id");
    }
  },
  getDoctorTypeList: async (data: any) => {
    try {
      const response = await Axios.post(`${DISCHARGE.DOCTOR_TYPE_LIST}`, data);

      return response.data;
    } catch (error) {
      throw error;
    }
  },
  getStarHealthRoomTypeList: async (portalMetaDataId: number) => {
    try {
      const response = await Axios.get(
        `${DISCHARGE.ROOM_TYPE_LIST}/${portalMetaDataId}`
      );

      return response.data;
    } catch (error) {
      console.error("Error Room Type data:", error);
      throw error;
    }
  },

  dischargeSummaryReader: async (formData: any) => {
    try {
      const res = await Axios.post(
        `${DOCUMENTS.DISCHARGE_SUMMARY_READER}`,
        formData
      );
      return res?.data;
    } catch (err: any) {
      console.error("Failed to Read Discharge Summary Document:", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to Read Discharge Summary Document";
      errorMessage(message);

      throw new Error(message);
    }
  },
};

export const dischargeServiceIhxNew = {
  getDischargeData: async (
    caseId: number | undefined,
    visitInfoId: number | undefined
  ) => {
    try {
      const response = await Axios.get(
        `${PATIENT_APIS.IHX_SERVICE}/${caseId}/visitInfo/${visitInfoId}/caseDischarges`
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching discharge data:", error);
      throw error;
    }
  },
  saveDischargeData: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalId: number | undefined,
    caseDischargeId: number | null,
    payload: any
  ): Promise<void> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.IHX_SERVICE}/${caseId}/visitInfo/${visitInfoId}/portalMetaData`;

      if (caseDischargeId === null) {
        // POST request
        response = await Axios.post(
          `${baseUrl}/${portalId}/caseDischarges`,
          payload
        );
      } else {
        // PATCH request
        response = await Axios.patch(
          `${baseUrl}/${portalId}/caseDischarge/${caseDischargeId}/caseDischarges`,
          payload
        );
      }

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to save enhancement data");
      }
    } catch (error) {
      console.error("Error saving enhancement data:", error);
      throw error;
    }
  },
  saveDischargeDataInEdgePro: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalId: number | undefined,
    caseDischargeId: number | null,
    payload: any
  ): Promise<any> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.IHX_SERVICE}/${caseId}/visitInfo/${visitInfoId}/portalMetaData`;
      // POST request
      if (caseDischargeId === null) {
        response = await Axios.post(
          `${baseUrl}/${portalId}/createCaseDischarge`,
          payload
        );
      } else {
        response = await Axios.patch(
          `${baseUrl}/${portalId}/caseDischarge/${caseDischargeId}/createCaseDischarge`,
          payload
        );
      }

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to save enhancement data");
      }

      return response;
    } catch (error) {
      console.error("Error saving enhancement data:", error);
      throw error;
    }
  },

  saveIntimateDischarge: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalMetaDataId: number | undefined,
    payload: any
  ): Promise<any> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.IHX_SERVICE}/${caseId}/visitInfo/${visitInfoId}/portalMetaData/${portalMetaDataId}/createCaseDischargeIntimation`;
      // POST request

      response = await Axios.post(`${baseUrl}`, payload);

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to save intimate discharge data");
      }
      return response;
    } catch (error) {
      console.error("Error saving intimate discharge data:", error);
      // throw error;
      throw new Error("Failed to save intimate discharge data");
    }
  },
  updateClaimId: async (
    formData: any,
    caseId: number | undefined,
    visitId: number | undefined,
    schemeId: number | undefined
  ): Promise<any> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.GET_PRE_AUTH_QUERY_DATA}/${caseId}/visitInfo/${visitId}/schemeId/${schemeId}`;

      response = await Axios.patch(`${baseUrl}`, formData);

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to update claim Id ");
      }
      return response;
    } catch (error) {
      console.error("Failed to update claim Id:", error);
      // throw error;
      throw new Error("Failed to update claim Id");
    }
  },
  getDoctorTypeList: async (data: any) => {
    try {
      const response = await Axios.post(
        `${DISCHARGE.DOCTOR_TYPE_LIST_NEW}`,
        data
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  },
  getStarHealthRoomTypeList: async (portalMetaDataId: number) => {
    try {
      const response = await Axios.get(
        `${DISCHARGE.ROOM_TYPE_LIST}/${portalMetaDataId}`
      );

      return response.data;
    } catch (error) {
      console.error("Error Room Type data:", error);
      throw error;
    }
  },

  dischargeSummaryReader: async (formData: any) => {
    try {
      const res = await Axios.post(
        `${DOCUMENTS.DISCHARGE_SUMMARY_READER}`,
        formData
      );
      return res?.data;
    } catch (err: any) {
      console.error("Failed to Read Discharge Summary Document:", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to Read Discharge Summary Document";
      errorMessage(message);

      throw new Error(message);
    }
  },
};

export const postPreAuthClaim = async (
  data: IClaimRes,
  caseId: number,
  visitId: number,
  portalId: number
): Promise<IClaimSubmissionResponse> => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.POST_CLAIM_CGHS_OP}/${caseId}/visitInfo/${visitId}/portalMetaData/${portalId}/caseClaimSubmission`,
      data
    );
    if (response.status === StatusCodes.OK) {
      return { claimSubmissionRes: response.data, status: response.status };
    } else {
      return {
        errorMessage: response?.data,
        status: response.status,
      };
    }
  } catch (error: any) {
    console.error("Cannot post claim details:", error?.response?.data);
    throw new Error(error?.response?.data || "Cannot post claim details");
  }
};

export const postPreAuthClaimIhx = async (
  data: IClaimRes,
  caseId: number,
  visitId: number,
  portalId: number
): Promise<IClaimSubmissionResponse> => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.IHX_SERVICE}/${caseId}/visitInfo/${visitId}/portalMetaData/${portalId}/caseClaimSubmission`,
      data
    );
    if (response.status === StatusCodes.OK) {
      return { claimSubmissionRes: response.data, status: response.status };
    } else {
      return {
        errorMessage: response?.data,
        status: response.status,
      };
    }
  } catch (error: any) {
    console.error("Cannot post claim details:", error?.response?.data);
    throw new Error(error?.response?.data || "Cannot post claim details");
  }
};

export const postPreAuthClaimToEdgePro = async (
  data: IClaimRes,
  caseId: number,
  visitId: number,
  portalId: number,
  caseClaimId: number | null
): Promise<IClaimSubmissionResponse> => {
  try {
    let response;
    const baseUrl = `${PATIENT_APIS.POST_CLAIM_CGHS_OP}/${caseId}/visitInfo/${visitId}/portalMetaData`;
    if (caseClaimId === null) {
      // POST request
      response = await Axios.post(
        `${baseUrl}/${portalId}/createCaseClaim`,
        data
      );
    } else {
      response = await Axios.patch(
        `${baseUrl}/${portalId}/caseClaim/${caseClaimId}/createCaseClaim`,
        data
      );
    }
    if (response.status === StatusCodes.OK) {
      return { claimSubmissionRes: response.data, status: response.status };
    } else {
      return {
        errorMessage: response?.data,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Cannot post claim details:", error);
    throw new Error("Cannot post claim details");
  }
};

export const postPreAuthClaimToEdgeProIhx = async (
  data: IClaimRes,
  caseId: number,
  visitId: number,
  portalId: number,
  caseClaimId: number | null
): Promise<IClaimSubmissionResponse> => {
  try {
    let response;
    const baseUrl = `${PATIENT_APIS.IHX_SERVICE}/${caseId}/visitInfo/${visitId}/portalMetaData`;
    if (caseClaimId === null) {
      // POST request
      response = await Axios.post(
        `${baseUrl}/${portalId}/createCaseClaim`,
        data
      );
    } else {
      response = await Axios.patch(
        `${baseUrl}/${portalId}/caseClaim/${caseClaimId}/createCaseClaim`,
        data
      );
    }
    if (response.status === StatusCodes.OK) {
      return { claimSubmissionRes: response.data, status: response.status };
    } else {
      return {
        errorMessage: response?.data,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Cannot post claim details:", error);
    throw new Error("Cannot post claim details");
  }
};

export const intimateClaimIhx = async (
  data: IClaimRes,
  caseId: number,
  visitId: number,
  portalId: number,
  caseClaimId: number | null
): Promise<IClaimSubmissionResponse> => {
  try {
    let response;
    const baseUrl = `${PATIENT_APIS.IHX_SERVICE}/${caseId}/visitInfo/${visitId}/portalMetaData/${portalId}/caseClaimIntimate`;
    response = await Axios.post(`${baseUrl}`, data);
    if (response.status === StatusCodes.OK) {
      return { claimSubmissionRes: response.data, status: response.status };
    } else {
      return {
        errorMessage: response?.data,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Cannot intimate claim details:", error);
    throw new Error("Cannot intimate claim details");
  }
};

export const patchPreAuthClaim = async (
  data: IClaimRes,
  caseId: number,
  visitId: number,
  portalId: number,
  claimSubmissionId: number
): Promise<IClaimSubmissionResponse> => {
  try {
    const response = await Axios.patch(
      `${PATIENT_APIS.POST_CLAIM_CGHS_OP}/${caseId}/visitInfo/${visitId}/portalMetaData/${portalId}/caseClaimSubmission/${claimSubmissionId}`,
      data
    );
    if (response.status === StatusCodes.OK) {
      return { claimSubmissionRes: response.data, status: response.status };
    } else {
      return {
        errorMessage: response?.data,
        status: response.status,
      };
    }
  } catch (error: any) {
    console.error("Cannot post claim details:", error?.response?.data);
    throw new Error(error?.response?.data || "Cannot post claim details");
  }
};

export const getPreAuthClaim = async (
  caseId: number,
  visitId: number,
  portalId: number
): Promise<IClaimSubmissionResponse> => {
  try {
    const response = await Axios.get(
      `${PATIENT_APIS.POST_CLAIM_CGHS_OP}/${caseId}/visitInfo/${visitId}/portalMetaData/${portalId}/caseClaimSubmission`
    );

    if (response.status === StatusCodes.OK) {
      return { claimSubmissionRes: response.data, status: response.status };
    } else {
      return {
        errorMessage: "Cannot post claim details",
        status: response.status,
      };
    }
  } catch (error: any) {
    console.error("Cannot post claim details:", error);
    throw new Error("Please, Try again later");
  }
};

export const getPreAuthClaimIHX = async (caseId: number, visitId: number) => {
  try {
    const response = await Axios.get(
      `${PATIENT_APIS.IHX_SERVICE}/${caseId}/visitInfo/${visitId}/caseClaimSubmission`
    );

    if (response.status === StatusCodes.OK) {
      return { claimSubmissionRes: response.data, status: response.status };
    } else {
      return {
        errorMessage: "Cannot get claim details",
        status: response.status,
      };
    }
  } catch (error: any) {
    console.error("Cannot get claim details:", error);
    throw new Error("Please, Try again later");
  }
};

export const fetchReferralList = async (patientId: number) => {
  try {
    const res = await Axios.get(
      `${PATIENT_APIS.GET_REFERRAL_DETAILS}/${patientId}/referralInfo`
    );
    return res.data;
  } catch (error) {
    console.error("Failed to get referral details:", error);
    errorMessage("Please, Try again after sometime");
    throw new Error("Failed to get referral details");
  }
};

export const updatePatientDetail = async (formData: any) => {
  try {
    const res = await Axios.patch(
      `${PATIENT_APIS.UPDATE_VISIT_REFERRAL}`,
      formData
    );
    return res.data;
  } catch (error) {
    console.error("Failed to update visit referral details:", error);
    errorMessage("Please, Try again after sometime");
    throw new Error("Failed to update visit referral details");
  }
};

export const fetchPortalData = async (caseId: number, visitId: number) => {
  try {
    const res = await Axios.get(
      `${PATIENT_APIS.GET_PORTAL_DATA}/${caseId}/visitInfo/${visitId}/getAllCasePreAuthByCaseIdAndVisitId`
    );
    return res.data;
  } catch (error) {
    console.error("Failed to get portalData:", error);
    throw new Error("Failed to get portal data");
  }
};

export const getPreAuthStepper = async (caseId: number, visitId: number) => {
  try {
    const res = await Axios.get(
      `${PATIENT_APIS.GET_STEPPER_DATA}?caseId=${caseId}&visitId=${visitId}`
    );
    return res.data;
  } catch (error) {
    console.error("Failed to get portalData:", error);
    throw new Error("Failed to get portal data");
  }
};

export const getPortalDataTemplate = async (
  caseId: number,
  visitId: number
) => {
  try {
    const res = await Axios.get(
      `${PATIENT_APIS.GET_STEPPER_DATA}?caseId=${caseId}&visitId=${visitId}`
    );
    return res.data;
  } catch (error) {
    console.error("Failed to get portalData:", error);
    throw new Error("Failed to get portal data");
  }
};

export const fetchCreditNoteData = async (visitId: number) => {
  try {
    const res = await Axios.get(`${PATIENT_APIS.CREDIT_NOTE_DATA}/${visitId}`);
    return res.data;
  } catch (error) {
    console.error("Failed to get credit note data:", error);
    throw new Error("Failed to get credit note data");
  }
};

export const postCreditNoteData = async (visitId: number, formData: any) => {
  try {
    const res = await Axios.put(
      `${PATIENT_APIS.CREDIT_NOTE_DATA}/${visitId}`,
      formData
    );
    return res.data;
  } catch (error) {
    console.error("Failed to post credit note data:", error);
    throw new Error("Failed to post credit note data");
  }
};

export const downloadDocument = async (fileName: string, url: string) => {
  try {
    const data = {
      s3Url: url,
    };

    let fileType: any = fileName?.includes(".")
      ? fileName.split(".").pop()
      : undefined;
    let urlType: any = url?.includes(".") ? url.split(".").pop() : undefined;
    const res: any = await Axios.post(`${DOCUMENTS.DOWNLOAD_DOCUMENT}`, data, {
      responseType: "blob",
    });
    const blob = new Blob([res.data], { type: res.headers["content-type"] });
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    // a.download = 'downloaded-file.pdf';
    a.download = fileType ? fileName : `${fileName}.${urlType}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    // return res.data;
  } catch (error) {
    console.error("Failed to download document:", error);
    throw new Error("Failed to download document");
  }
};

export const downloadReport = async (formData: any) => {
  try {
    const res: any = await axios.post(
      `${DOCUMENTS.DOWNLOAD_REPORT}`,
      formData,
      {
        responseType: "blob",
      }
    );

    const blob = new Blob([res.data], { type: res.headers["content-type"] });
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = `${formData.report}_${formData.scheme}_${formData?.hospitalState}.xls`;
    // a.download = `${fileName}`;
    document.body.appendChild(a);
    a.click();
    // window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    // return res.data;
  } catch (error) {
    console.error("Failed to download report:", error);
    errorMessage("Please, Try Again After sometime");
    throw new Error("Failed to download report");
  }
};

export const downloadReportNew = async (formData: any) => {
  try {
    const res: any = await axios.post(
      `${DOCUMENTS.DOWNLOAD_REPORT_NEW}`,
      formData,
      {
        responseType: "blob",
      }
    );

    const blob = new Blob([res.data], { type: res.headers["content-type"] });
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = `${formData.report}_${formData.scheme}_${formData?.hospitalState}.xls`;
    // a.download = `${fileName}`;
    document.body.appendChild(a);
    a.click();
    // window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    // return res.data;
  } catch (error) {
    console.error("Failed to download report:", error);
    errorMessage("Please, Try Again After sometime");
    throw new Error("Failed to download report");
  }
};

export const fetchReferralById = async (referralId: number) => {
  try {
    const res = await Axios.get(
      `${PATIENT_APIS.GET_REFERRAL_DETAIL_BY_ID}/${referralId}`
    );
    return res.data.referralInfoResponse;
  } catch (error) {
    console.error("Failed to get referral details:", error);
    errorMessage("Please, Try again after sometime");
    throw new Error("Failed to get referral details");
  }
};

export const deleteUploadedDocument = async (deleteForm: any) => {
  try {
    const res = await Axios.put(`${DOCUMENTS.DELETE_DOCUMENT}`, deleteForm);
    if (res?.status === StatusCodes.OK) {
      successMessage("Document Deleted successfully");
    }
    return res.data;
  } catch (error) {
    console.error("Failed to delete document:", error);
    errorMessage("Cannot Delete document. Please, Try again after sometime");
    throw new Error("Failed to delete document");
  }
};

export const dischargeSummary = {
  getProcedureList: async (data: any) => {
    try {
      const response = await Axios.post(
        `${DISCHARGE_SUMMARY.PROCEDURE_FIELD_LIST}`,
        data
      );

      if (response.status === StatusCodes.OK) {
        // const parsedOutput = cleanAndParseJSON(response.data);
        const parsedOutput = response.data;
        return parsedOutput;
      } else {
        return {
          errorMessage: response?.data,
          status: response.status,
        };
      }
    } catch (error) {
      console.error("Error Procedure Fields Data:", error);
      throw error;
    }
  },
  downloadDischargeSummaryPDF: async (data: any) => {
    try {
      // const data = {
      //   s3Url: url,
      // };
      // let urlType: Array<any> = url?.split(".");
      // urlType = urlType[urlType?.length - 1];
      const res: any = await Axios.post(
        `${DISCHARGE_SUMMARY.DISCHARGE_SUMMARY_PDF}`,
        data
        //   {
        //   responseType: "blob",
        // }
      );
      // Decode the Base64 blob and save it as a file
      const base64String = res.data;
      const binaryData = atob(base64String);
      const byteArray = new Uint8Array(binaryData.length);

      for (let i = 0; i < binaryData.length; i++) {
        byteArray[i] = binaryData.charCodeAt(i);
      }

      // Create a Blob from the byte array
      const fileBlob = new Blob([byteArray], { type: "application/pdf" });

      // Create a download link for the file
      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(fileBlob);
      downloadLink.download = "file.pdf";
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error) {
      console.error("Failed to get discharge summary pdf:", error);
      throw new Error("Failed to get discharge summary pdf");
    }
  },
};

export const getQuestionsDeficiencyCheck = async (documentId: number) => {
  try {
    const res = await Axios.get(`${DOCUMENTS.DEFICIENCY_CHECK}/${documentId}`);
    return res.data;
  } catch (error: any) {
    console.error("Failed to get document deficiency questions:", error);
    errorMessage(
      error?.response?.data ||
        "Cannot Get document deficiency question. Please, Try again after sometime"
    );
    throw new Error("Failed to get document deficiency questions");
  }
};

export const postQuestionDeficiencyCheck = async (deficiencyForm: any) => {
  try {
    const res = await Axios.post(
      `${DOCUMENTS.POST_DEFICIENCY_CHECK}`,
      deficiencyForm
    );
    return res.data;
  } catch (error: any) {
    console.error("Failed to post document deficiency questions:", error);
    errorMessage(
      error?.response?.data ||
        "Cannot Post document deficiency question. Please, Try again after sometime"
    );
    throw new Error("Failed to Post document deficiency questions");
  }
};

export const getSavedDocumentDeficiencyCheck = async (documentId: number) => {
  try {
    const res = await Axios.get(
      `${DOCUMENTS.GET_DOCUMENT_DEFICIENCY_CHECK}/${documentId}`
    );
    return res.data;
  } catch (error: any) {
    console.error("Failed to get document deficiency saved data:", error);
    errorMessage(
      error?.response?.data ||
        "Cannot Get document deficiency saved data. Please, Try again after sometime"
    );
    throw new Error("Failed to Post document deficiency saved data");
  }
};

export const fetchPatientDetailsUHID = async (
  formData: any,
  schemeMetaDataId: number
): Promise<any> => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.SEARCH_PATIENT}/${schemeMetaDataId}/withUHID`,
      formData
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching patient details using uhid:", error);
    throw new Error("Cannot fetch patient details using UHID");
  }
};

export const fetchCreditNumbers = async (caseId: number): Promise<any> => {
  try {
    const response = await Axios.get<any>(
      `${PATIENT_APIS.CREDIT_NUMBER}/${caseId}/creditNumbers`
    );

    return { schemeNames: response.data, status: response.status };
  } catch (error) {
    console.error("Error fetching credit Number", error);
    throw new Error("Cannot fetch credit Number");
  }
};

export const fetchProcedures = async (data: any): Promise<any> => {
  try {
    const response = await Axios.post(`${PROCEDURES.PROCEDURES_LIST}`, data);
    return response.data;
  } catch (error) {
    console.error("Error Type data:", error);
    throw error;
  }
};
export const fetchSpecialities = async (): Promise<any> => {
  try {
    const response = await Axios.get(`${PROCEDURES.SPECIALITY_LIST}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching credit Number", error);
    throw new Error("Cannot fetch credit Number");
  }
};

export const fetchProceduresNew = async (data: any): Promise<any> => {
  try {
    const response = await Axios.post(
      `${PROCEDURES.PROCEDURES_LIST_NEW}`,
      data
    );
    return response.data;
  } catch (error) {
    console.error("Error Type data:", error);
    throw error;
  }
};
export const fetchSpecialitiesNew = async (): Promise<any> => {
  try {
    const response = await Axios.get(`${PROCEDURES.SPECIALITY_LIST_NEW}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching credit Number", error);
    throw new Error("Cannot fetch credit Number");
  }
};

export const getSettlement = async (caseId: number, visitId: number) => {
  try {
    const response = await Axios.get(
      `${CASES_APIS.CASE_DETAILS}/${caseId}/visitInfoId/${visitId}/settlement`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateSettlement = async (
  formData: any,
  caseId: number,
  visitId: number
) => {
  try {
    const response = await Axios.post(
      `${CASES_APIS.CASE_DETAILS}/${caseId}/visitInfoId/${visitId}/settlement`,
      formData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating settlement:", error);
    throw error;
  }
};

//to get procedure amount when procedure is selected in cghs/capf enhancement

export const getProcedureAmount = async (formData: any) => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.GET_PROCEDURE_AMOUNT}`,
      formData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProcedureAmountNew = async (formData: any) => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.GET_PROCEDURE_AMOUNT_NEW}`,
      formData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

//to get strats option of procedure added in enhancement of cghs/capf
export const getStratsData = async (formData: any) => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.GET_PROCEDURE_STRATS}`,
      formData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

//to get strats option of procedure added in enhancement of cghs/capf
export const getStratsDataNew = async (formData: any) => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.GET_PROCEDURE_STRATS_NEW}`,
      formData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

//to fetch procedures in enhancement of cghs/capf
export const fetchEnhancementProcedure = async (
  caseId: number,
  visitId: number,
  schemeId: number
) => {
  try {
    const response = await Axios.get(
      `${PATIENT_APIS.FETCH_ENHANCEMENT_PROCEDURE}/${caseId}/visitInfo/${visitId}/schemeId/${schemeId}/procedures`
    );
    return response.data;
  } catch (error) {
    throw new Error("Procedures are not getting fetched");
  }
};

// to add and delete procedure
export const upsertEnhancementProcedure = async (
  portalMetaDataId: number,
  formData: any
) => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.ENHANCEMENT_PROCEDURE}/${portalMetaDataId}/enhancementProcedure`,
      formData
    );
    return response.data;
  } catch (error) {
    // throw error;
    throw new Error("Cannot SUBMIT Enhancement");
  }
};

//to delete procedures fetched from portal in enhancement of cghs/capf
export const deleteEnhancementProcedure = async (
  portalMetaDataId: number,
  formData: any
) => {
  try {
    const response = await Axios.patch(
      `${PATIENT_APIS.ENHANCEMENT_PROCEDURE}/${portalMetaDataId}/CGHSEnhancementProcedure`,
      formData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

//submit registration id and claim id updation

export const submitId = async (
  caseId: number,
  visitId: number,
  schemeId: number,
  formData: any
) => {
  try {
    const res = await Axios.patch(
      `${PATIENT_APIS.SUBMIT_ID}/${caseId}/visitInfo/${visitId}/schemeId/${schemeId}`,
      formData
    );
    return res.data;
  } catch (error) {
    throw new Error("Failed to Update Id");
  }
};

//for portal registration
export const registerPatientPortal = async (
  portalMetaDataId: number,
  data: any
) => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.REGISTER_PORTAL}/${portalMetaDataId}/patientPortalRegistration`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data || "Unable to do Portal Registration"
    );
  }
};

//submit registration id and claim id updation

// export const submitId = async (
//   caseId: number,
//   visitId: number,
//   schemeId: number,
//   formData: any
// ) => {
//   try {
//     const res = await Axios.patch(
//       `${PATIENT_APIS.SUBMIT_ID}/${caseId}/visitInfo/${visitId}/schemeId/${schemeId}`,
//       formData
//     );
//     return res.data;
//   } catch (error) {
//     throw new Error("Failed to Update Id");
//   }
// };

//for portal registration
// export const registerPatientPortal = async (
//   portalMetaDataId: number,
//   data: any
// ) => {
//   try {
//     const response = await Axios.post(
//       `${PATIENT_APIS.REGISTER_PORTAL}/${portalMetaDataId}/patientPortalRegistration`,
//       data
//     );
//     return response.data;
//   } catch (error: any) {
//     throw new Error(
//       error?.response?.data || "Unable to do Portal Registration"
//     );
//   }
// };

//approval api
export const submitApproval = async (formData: any) => {
  try {
    const res = await Axios.post(`${PATIENT_APIS.APPROVE_PREAUTH}`, formData);
    return res.data;
  } catch (error) {
    throw new Error("Failed to Approve");
  }
};

export const submitApprovalIhx = async (formData: any) => {
  try {
    const res = await Axios.post(
      `${PATIENT_APIS.APPROVE_PREAUTH}/ihx/approve`,
      formData
    );
    return res.data;
  } catch (error) {
    throw new Error("Failed to Approve");
  }
};

export const submitApprovalCghs = async (formData: any) => {
  try {
    const res = await Axios.post(
      `${PATIENT_APIS.APPROVE_PREAUTH}/cghs/approve`,
      formData
    );
    return res.data;
  } catch (error) {
    throw new Error("Failed to Approve");
  }
};

export const submitApprovalCghsNew = async (formData: any) => {
  try {
    const res = await Axios.post(
      `${PATIENT_APIS.APPROVE_PREAUTH}/cghs_new/approve`,
      formData
    );
    return res.data;
  } catch (error) {
    throw new Error("Failed to Approve");
  }
};

export const submitApprovalAyushman = async (formData: any) => {
  try {
    const res = await Axios.post(
      `${PATIENT_APIS.APPROVE_PREAUTH}/ayushmanBharat/approve`,
      formData
    );
    return res.data;
  } catch (error) {
    throw new Error("Failed to Approve");
  }
};

export const submitApprovalStarhealth = async (formData: any) => {
  try {
    const res = await Axios.post(
      `${PATIENT_APIS.APPROVE_PREAUTH}/star-health/approve`,
      formData
    );
    return res.data;
  } catch (error) {
    throw new Error("Failed to Approve");
  }
};

//assigned user api
export const assignedTo = async (visitInfoId: number) => {
  try {
    const res = await Axios.post(
      `${PATIENT_APIS.ASSIGNED_USER}/${visitInfoId}`
    );
    return res.data;
  } catch (error) {
    throw new Error("Failed to Assigned a user");
  }
};

export const getPreAuthClaimECHS = async (caseId: number, visitId: number) => {
  try {
    const response = await Axios.get(
      `/api/v1/case/${caseId}/visit/${visitId}/claim/echs`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const handleDownloadExcel = async (urlLink: any) => {
  try {
    const response = await fetch(urlLink);
    const blob = await response.blob();

    // Try to get filename from response headers
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = "download.xlsx"; // fallback
    if (contentDisposition && contentDisposition.includes("filename=")) {
      filename = contentDisposition
        .split("filename=")[1]
        .replace(/['"]/g, "")
        .trim();
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Failed to download:", err);
  }
};

export const saveTotalBillAmount = async (
  caseId: number,
  visitId: number,
  totalBillAmount: any
) => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.CGHS_DISCHARGE_TOTAL_BILL_AMOUNT}/${caseId}/visitInfo/${visitId}/totalBillAmount/${totalBillAmount}`
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to save total bill amount");
  }
};

export const saveTotalBillAmountNew = async (
  caseId: number,
  visitId: number,
  totalBillAmount: any
) => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.CGHS_NEW_DISCHARGE_TOTAL_BILL_AMOUNT}/${caseId}/visitInfo/${visitId}/totalBillAmount/${totalBillAmount}`
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to save total bill amount");
  }
};

export const getReferralChecklist = async (caseId: any, visitId: any) => {
  try {
    const response = await Axios.get(
      `${PATIENT_APIS.REFERRAL_CHECKLIST}/referral/caseId/${caseId}/visitId/${visitId}`
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to get referral checklist");
  }
};

export const postFOReferralChecklist = async (id: any) => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.REFERRAL_CHECKLIST}/${id}/fo-check`
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to post fo referral checklist");
  }
};

export const postMOReferralChecklist = async (id: any) => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.REFERRAL_CHECKLIST}/${id}/mo-check`
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to post mo referral checklist");
  }
};

export const downloadExcelQL = async (filters: any, healthCareId: number) => {
  try {
    const response = await Axios.get(PATIENT_APIS.DOWNLOAD_PATIENTS_QL_EXCEL, {
      headers: {
        "Content-Type": "application/json",
        endDate: filters.endDate,
        orderBy: filters.orderBy,
        search: filters.searchTerm || "",
        startDate: filters.startDate,
        state: filters.state ? filters.state.map(String) : null,
        scheme: filters.scheme ? filters.scheme.map(String) : null,
        visitType: filters.visitType
          ? filters.visitType.map(String)
          : undefined,
        status: filters.status?.toString(),
        stage: filters.stage,
        selectedDateType: filters.selectedDateType,
        healthCareId: healthCareId,
        all: Number(healthCareId) === -1 ? "yes" : "no",
        typeOfQuery: filters.typeOfQuery,
        // assignedToMe: filters.assignedToMe || "no",
      },
      responseType: "arraybuffer",
    });

    const contentDisposition = response.headers["content-disposition"];
    const filename = contentDisposition
      ? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
      : "queries_data.xlsx";

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error in downloading excel of all queries:", error);
    throw new Error("Error in downloading excel of all queries");
  }
};

export const getMailTimeline = async (id: any) => {
  try {
    const response = await Axios.get(`${PATIENT_APIS.MAIL_TIMELINE}/${id}`);
    return response.data;
  } catch (error) {
    throw new Error("Failed to get mail timeline");
  }
};

export const getItemsApproveByScheme = async (
  visitId: number,
  isTPAScheme: boolean
) => {
  try {
    const endpoint = isTPAScheme
      ? `${API_URL}/api/tpaBilling/getLatestBillItems`
      : `${API_URL}/api/cghsBilling/getLatestBillItems`;

    const payload = {
      visitId,
      billApprovalStatus: "ALL",
    };

    const response = await Axios.post(endpoint, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateItemsApproveByScheme = async (
  visitId: number,
  isTPAScheme: boolean,
  data: any[]
) => {
  try {
    const endpoint = isTPAScheme
      ? `${API_URL}/api/tpaBilling/updateLatestBillItems`
      : `${API_URL}/api/cghsBilling/updateLatestBillItems`;

    const payload = {
      billItemsRequestList: data,
    };

    const response = await Axios.post(endpoint, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllMails = async (data: any) => {
  try {
    const response = await Axios.post(`${API_URL}/api/emails/getEmails`, data);
    return response.data;
  } catch (error) {
    throw new Error("Failed to get mails");
  }
};

export const updateEmailData = async (id: any, data: any) => {
  try {
    const response = await Axios.patch(`${API_URL}/api/emails/updateData/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error("Failed to update mails");
  }
};

export const getEmailData = async (reqPayload: any) => {
  try {
    const response = await Axios.post(`${API_URL}/api/emails/getEmailData`, reqPayload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const postBeneficiaryDetails = async (formData: any) => {
  try {
    const response = await Axios.post(`${PATIENT_APIS.CGHS_NEW_BENEFICIARY}/fetch`, formData);
    return response.data;
  } catch (error) {   
    console.error("Error fetching CGHS New beneficiary details:", error);
    throw new Error("Cannot fetch CGHS New beneficiary details");
  }
};

export const getPatientDetailsByBeneficiaryId = async (beneficiaryId: string) => {
  try {
    const response = await Axios.get(`${PATIENT_APIS.CGHS_NEW_BENEFICIARY}/${beneficiaryId}`);
    return response.data;
  } catch (error) {
    console.error("Error getting CGHS New beneficiary details:", error);
    throw new Error("Cannot get CGHS New beneficiary details");
  }
};

export const verifyOtpDetails = async (caseId: any, visitId: any, payload: any) => {
  try {
    const response = await Axios.post(`${PATIENT_APIS.PORTAL_REG_CGHS_NEW}/caseId/${caseId}/visitId/${visitId}/otp_verify`, payload);
    return response.data;
  } catch (error) {
    console.error("Error in verifying otp details:", error);
    throw new Error("Cannot get Verify otp details");
  }
};

export const registerPortalDetails = async (caseId: any, visitId: any, payload: any) => {
  try {
    const response = await Axios.post(`${PATIENT_APIS.PORTAL_REG_CGHS_NEW}/caseId/${caseId}/visitId/${visitId}/register`, payload);
    return response.data;
  } catch (error) {
    console.error("Error in registering details:", error);
    throw new Error("Cannot register portal details");
  }
};

